import { FREE_PLAN_HANDLE } from 'constants/billing';
import { UpsellKey } from 'features/google-tag-manager/enum/upsell-key';
import useSendGTMAction from 'features/google-tag-manager/hooks/useSendGTMAction';
import { UpsellClickedGtmAction } from 'features/google-tag-manager/model/actions/upsell_clicked.gtm-action';
import usePlanHandle from 'hooks/billing/usePlanHandle';
import useShopifySubscription from 'hooks/billing/useShopifySubscription';
import {
    AppBillingInterval,
    AppInstallationSubscription,
} from 'hooks/shopify/queries/get-all-app-subscriptions.graphql';
import { SubscriptionStatus } from 'models/SubscriptionStatus';
import { useCallback, useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';
import {
    selectShopifyAllSubscriptions,
    selectShopifyAppHandle,
    selectShopifyAppPlans,
} from 'redux/slices/shopify-app-pricing/shopify-app-pricing.selector';
import { Addon } from '../../../pb/subscription/v1/addon_pb';
import { ShopifyPlan } from '../../../pb/subscription/v1/shopify_plan_pb';

interface CurrentSubscriptionHelper {
    appHandle: string | undefined;
    currentSubscription: AppInstallationSubscription | undefined;
    /**
     * The Shopify pricing plan the shop is on: the current subscription's plan, or the free plan when the
     * shop has no subscription at all. `undefined` for a legacy subscription (no plan handle) or while loading.
     */
    currentPlan: ShopifyPlan | undefined;
    /** Every subscription of the app installation, current and past, newest first */
    allSubscriptions: AppInstallationSubscription[];
    isLoading: boolean;
    /** Whether the current subscription is a legacy (manual pricing) one without a plan handle; false without a subscription */
    isLegacy: boolean;
    /** Whether the current plan is one of the legacy Shopify App Pricing plans (its handle contains `legacy-`) */
    isShopifyAppPricingLegacy: boolean;
    isCancelled: boolean;
    /** Whether the current plan is usage-based, from the plan details' `usageBased` */
    isUsageBased: boolean;
    /** Addons of `currentPlan`, so a shop without a subscription gets the free plan's addons */
    addons: Addon[];
    planHandle: string | undefined;
    /** Every Shopify pricing plan of the app */
    plans: ShopifyPlan[];
    /** Opens the pricing plans page in the Shopify admin; with an `upsellKey`, also sends the `upsell_clicked` GTM event */
    goToPLans: (upsellKey?: UpsellKey) => void;
    /**
     * Opens one plan's page in the Shopify admin, e.g. to subscribe to it; monthly billing by default.
     * With an `upsellKey`, also sends the `upsell_clicked` GTM event.
     */
    goToPlan: (handle: string, options?: GoToPlanOptions) => void;
    goToBilling: () => void;
}

interface GoToPlanOptions {
    interval?: AppBillingInterval;
    upsellKey?: UpsellKey;
}

const useCurrentSubscriptionHelper = (): CurrentSubscriptionHelper => {
    const shopifyPlans = useAppSelector(selectShopifyAppPlans);
    const appHandle = useAppSelector(selectShopifyAppHandle);
    const { currentSubscription } = useShopifySubscription();
    const allSubscriptions = useAppSelector(selectShopifyAllSubscriptions);
    const { planHandle, isLoading } = usePlanHandle();
    const sendGTMAction = useSendGTMAction();

    const sendUpsellClicked = useCallback(
        (upsellKey: UpsellKey | undefined): void => {
            if (upsellKey) sendGTMAction(new UpsellClickedGtmAction({ name: upsellKey }));
        },
        [sendGTMAction]
    );

    // A shop without any subscription is on the free plan, so its limits and addons apply
    const currentPlanDetails = useMemo(() => {
        const handle = currentSubscription ? planHandle : FREE_PLAN_HANDLE;
        return shopifyPlans?.plans.find((p) => p.handle === handle);
    }, [currentSubscription, planHandle, shopifyPlans]);

    const isLegacy = useMemo(() => {
        return !!currentSubscription && !planHandle;
    }, [currentSubscription, planHandle]);

    const isShopifyAppPricingLegacy = useMemo(() => !!planHandle?.includes('legacy-'), [planHandle]);

    const isCancelled = useMemo(() => {
        return currentSubscription?.status === SubscriptionStatus.CANCELLED;
    }, [currentSubscription]);

    const isUsageBased = useMemo(() => !!currentPlanDetails?.usageBased, [currentPlanDetails]);

    const addons = useMemo(() => {
        return currentPlanDetails?.addons || [];
    }, [currentPlanDetails]);

    const plans = useMemo(() => shopifyPlans?.plans ?? [], [shopifyPlans]);

    const goToPLans = useCallback(
        (upsellKey?: UpsellKey) => {
            sendUpsellClicked(upsellKey);
            open(`shopify://admin/charges/${appHandle}/pricing_plans`, '_top');
        },
        [appHandle, sendUpsellClicked]
    );

    const goToPlan = useCallback(
        (handle: string, { interval = 'EVERY_30_DAYS', upsellKey }: GoToPlanOptions = {}) => {
            sendUpsellClicked(upsellKey);
            open(`shopify://admin/charges/${appHandle}/plans/${handle}?${new URLSearchParams({ interval })}`, '_top');
        },
        [appHandle, sendUpsellClicked]
    );

    const goToBilling = useCallback(
        () => open(`shopify://admin/settings/apps/app_installations/app/${appHandle}`, '_top'),
        [appHandle]
    );

    return {
        appHandle,
        currentSubscription,
        currentPlan: currentPlanDetails,
        allSubscriptions,
        isLoading,
        isLegacy,
        isShopifyAppPricingLegacy,
        isCancelled,
        isUsageBased,
        addons,
        planHandle,
        plans,
        goToPLans,
        goToPlan,
        goToBilling,
    };
};

export default useCurrentSubscriptionHelper;
